import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Target,
  Cpu,
  TrendingUp,
  Award,
  MapPin,
  Mail,
  Linkedin,
  GraduationCap,
  Briefcase,
  Search,
  Map,
  PenTool,
  Wrench,
  Rocket,
  RefreshCw,
  Coffee,
  X,
  Download,
  Share2,
  FileText,
  Calendar
} from 'lucide-react';
import Engagements from './Engagements';

// Custom hook for scroll animations
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

const PortfolioHome = () => {
  const [isPageVisible, setIsPageVisible] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // Section animation refs
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [pillarsRef, pillarsVisible] = useScrollAnimation(0.1);
  const [techRef, techVisible] = useScrollAnimation(0.1);
  const [credentialsRef, credentialsVisible] = useScrollAnimation(0.1);
  const [contactRef, contactVisible] = useScrollAnimation(0.1);

  useEffect(() => {
    setIsPageVisible(true);
    window.scrollTo(0, 0);

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      // Track navigation clicks
      if (window.gtag) {
        window.gtag('event', 'navigation_click', {
          section_name: sectionId
        });
      }
    }
  };

  // Strategy items
  const strategyItems = [
    "Executive discovery workshops with Sales, Marketing, IT, and leadership",
    "Current state assessments and capability gap analysis",
    "Future state vision and reference architecture",
    "Customer journey mapping and persona development",
    "Process mapping and documentation",
    "Technology roadmaps with prioritized initiatives",
    "Use case identification and prioritization",
    "AI readiness assessments and Copilot adoption coaching",
    "RFP development and vendor evaluation"
  ];

  // Systems items
  const systemsItems = [
    "Salesforce CRM with custom objects and workflows",
    "Pardot marketing automation and lead scoring",
    "ZoomInfo, Sage ERP, and platform integrations",
    "FIDO2 passwordless authentication rollout",
    "Prisma Access VPN / GlobalProtect deployment",
    "eCommerce websites (Shopify, WordPress, nopCommerce)",
    "Voice AI agents with Retell and n8n",
    "Custom MCP servers and Python automation",
    "PostgreSQL and Airtable database builds",
    "API integrations connecting CRM, calendars, SMS"
  ];

  // Results items
  const resultsItems = [
    "700+ daily AI recruiting calls with 50%+ cost reduction",
    "100+ vacation rental properties automated through scheduling platform",
    "13,000+ users migrated to FIDO2 passwordless authentication ($500K→$1.3M program)",
    "Salesforce deployed across 19 branches with 300+ users, doubling sales conversion",
    "$750K in change orders secured through CRM roadmap expansion",
    "35+ stakeholder interviews and 23+ hours of workshops for enterprise assessments",
    "Technology roadmaps, future state architectures, and 5 CDP use cases delivered",
    "GenAI curriculum (100/200/300 levels) developed"
  ];

  // Tech categories
  const techCategories = [
    {
      title: "AI & Automation",
      items: ["OpenAI", "Claude", "Retell AI", "n8n", "Make", "Python", "MCP", "RAG", "Cursor"]
    },
    {
      title: "Data & Platforms",
      items: ["PostgreSQL", "MySQL", "Airtable", "Salesforce", "Zoho", "Pardot", "Adobe Experience Platform", "Datorama", "Azure DevOps"]
    },
    {
      title: "Infrastructure & Security",
      items: ["Prisma Access", "GlobalProtect", "FIDO2", "Entra ID", "Linux"]
    },
    {
      title: "Strategy & Delivery",
      items: ["Jira", "Confluence", "Miro", "Lucidchart", "Smartsheet", "Microsoft 365", "Google Workspace"]
    }
  ];

  // Certifications
  const certifications = [
    "Project Management Professional (PMP)®",
    "SAFe® Scrum Master",
    "Certified Scrum Master (CSM)",
    "Certified Scrum Product Owner (CSPO)",
    "Salesforce Certified AI Associate",
    "Salesforce Certified Business Analyst"
  ];


  return (
    <>
      {/* CSS Keyframes for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
      <div className={`min-h-screen bg-white transition-opacity duration-1000 ${isPageVisible ? 'opacity-100' : 'opacity-0'}`} style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Navigation Bar */}
      <nav className="bg-white border-b border-gray-100 py-4 px-6 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Coffee className="w-5 h-5" style={{ color: '#2d8a8a' }} />
            <span className="text-xl font-semibold" style={{ color: '#1a2332', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Boris Blinderman</span>
          </div>
          <div className="hidden md:flex space-x-8">
            <button
              className="text-gray-600 hover:text-[#2d8a8a] transition-colors duration-300"
              onClick={() => scrollToSection('pillars')}
            >
              Approach
            </button>
            <button
              className="text-gray-600 hover:text-[#2d8a8a] transition-colors duration-300"
              onClick={() => scrollToSection('engagements')}
            >
              Engagements
            </button>
            <button
              className="text-gray-600 hover:text-[#2d8a8a] transition-colors duration-300"
              onClick={() => scrollToSection('contact')}
            >
              Contact
            </button>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://linkedin.com/in/BorisBlinderman"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-[#2d8a8a] transition-colors duration-300"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'linkedin_click', {
                    location: 'navigation'
                  });
                }
              }}
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <button
              onClick={() => scrollToSection('engagements')}
              className="text-white px-4 py-2 rounded text-sm font-medium flex items-center transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
              style={{ backgroundColor: '#2d8a8a' }}
            >
              View Work
              <ChevronDown className="ml-1 h-4 w-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div
        ref={heroRef}
        className="py-16 md:py-24 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #e8f4f4 100%)' }}
      >
        {/* Decorative gradient blobs */}
        <svg
          className="absolute -left-20 top-10 w-72 h-72 opacity-30"
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: 'blur(40px)', animation: 'float 8s ease-in-out infinite' }}
        >
          <defs>
            <linearGradient id="blob1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#2d8a8a', stopOpacity: 0.6 }} />
              <stop offset="100%" style={{ stopColor: '#1a2332', stopOpacity: 0.3 }} />
            </linearGradient>
          </defs>
          <path fill="url(#blob1)" d="M45.2,-51.2C59.3,-42.8,72,-28.9,75.4,-12.8C78.8,3.3,72.9,21.6,62.1,35.2C51.3,48.8,35.6,57.7,18.5,63.1C1.4,68.5,-17.1,70.4,-33.1,64.5C-49.1,58.6,-62.6,44.9,-69.8,28.2C-77,11.5,-77.9,-8.2,-71.4,-24.7C-64.9,-41.2,-51,-54.5,-36,-63.1C-21,-71.7,-4.9,-75.6,9.4,-72.1C23.7,-68.6,31.1,-59.6,45.2,-51.2Z" transform="translate(100 100)" />
        </svg>
        <svg
          className="absolute -right-16 top-32 w-64 h-64 opacity-20"
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: 'blur(50px)', animation: 'float 10s ease-in-out infinite', animationDelay: '-3s' }}
        >
          <defs>
            <linearGradient id="blob2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#2d8a8a', stopOpacity: 0.5 }} />
              <stop offset="100%" style={{ stopColor: '#4a9e9e', stopOpacity: 0.3 }} />
            </linearGradient>
          </defs>
          <path fill="url(#blob2)" d="M39.5,-48.3C52.9,-40.7,66.5,-30.5,71.2,-16.9C75.9,-3.3,71.7,13.7,63.3,27.4C54.9,41.1,42.3,51.5,28.1,57.2C13.9,62.9,-1.9,63.9,-17.4,60.1C-32.9,56.3,-48.1,47.7,-57.1,35C-66.1,22.3,-68.9,5.5,-65.8,-9.5C-62.7,-24.5,-53.7,-37.7,-42,-47.2C-30.3,-56.7,-15.2,-62.5,-0.6,-61.8C14,-61.1,26.1,-55.9,39.5,-48.3Z" transform="translate(100 100)" />
        </svg>
        <svg
          className="absolute right-10 -bottom-10 w-48 h-48 opacity-15 hidden md:block"
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: 'blur(30px)', animation: 'float 7s ease-in-out infinite', animationDelay: '-5s' }}
        >
          <defs>
            <linearGradient id="blob3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#1a2332', stopOpacity: 0.4 }} />
              <stop offset="100%" style={{ stopColor: '#2d8a8a', stopOpacity: 0.2 }} />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="80" fill="url(#blob3)" />
        </svg>

        <div className={`max-w-6xl mx-auto px-6 relative z-10 transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
            {/* Profile Photo with Gradient Border */}
            <div className="flex-shrink-0">
              <div
                className="p-[4px] rounded-2xl transition-all duration-500 hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #2d8a8a, #1a2332)',
                  backgroundSize: '200% 200%'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundPosition = '100% 100%'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundPosition = '0% 0%'}
              >
                <div className="w-64 h-80 md:w-72 md:h-96 rounded-xl overflow-hidden">
                  <img
                    src="/Boris_Profile.png"
                    alt="Boris Blinderman - AI Strategy and Production AI Systems Consultant"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center 10%' }}
                    loading="lazy"
                    width="288"
                    height="384"
                  />
                </div>
              </div>
            </div>

            {/* Hero Content */}
            <div className="flex-1 text-center md:text-left">
              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-bold mb-5 tracking-tight leading-tight"
                style={{ color: '#1a2332', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                From Roadmap<br className="hidden md:block" /> to Production
              </h1>
              <p className="text-lg md:text-xl mb-6 max-w-xl" style={{ color: '#555' }}>
                Enterprise transformation consulting from strategic vision into production systems and organizational adoption.
              </p>

              {/* Quick highlights */}
              <div className="flex flex-wrap gap-3 mb-8 justify-center md:justify-start">
                <span className="text-sm px-4 py-2 rounded-full" style={{ backgroundColor: '#e8f4f4', color: '#2d8a8a' }}>
                  AI Strategy & Implementation
                </span>
                <span className="text-sm px-4 py-2 rounded-full" style={{ backgroundColor: '#e8f4f4', color: '#2d8a8a' }}>
                  Production AI Systems
                </span>
                <span className="text-sm px-4 py-2 rounded-full" style={{ backgroundColor: '#e8f4f4', color: '#2d8a8a' }}>
                  Enterprise Program Delivery
                </span>
              </div>

              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <button
                  onClick={() => scrollToSection('engagements')}
                  className="text-white px-6 py-3 rounded-lg font-medium flex items-center transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  style={{ backgroundColor: '#2d8a8a' }}
                >
                  View Work
                  <ChevronDown className="ml-2 h-5 w-5" />
                </button>
                <button
                  onClick={() => {
                    setShowScheduleModal(true);
                    if (window.gtag) {
                      window.gtag('event', 'schedule_meeting_click', {
                        location: 'hero'
                      });
                    }
                  }}
                  className="border-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5 flex items-center"
                  style={{ borderColor: '#1a2332', color: '#1a2332' }}
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  Schedule a Meeting
                </button>
                <button
                  onClick={() => {
                    setShowResumeModal(true);
                    if (window.gtag) {
                      window.gtag('event', 'resume_modal_open', {
                        location: 'hero'
                      });
                    }
                  }}
                  className="border-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5 flex items-center"
                  style={{ borderColor: '#2d8a8a', color: '#2d8a8a' }}
                >
                  <FileText className="mr-2 h-5 w-5" />
                  Resume
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Simple spacer */}
      <div className="h-8" style={{ backgroundColor: '#ffffff' }} />

      {/* Three Pillars Section */}
      <div
        ref={pillarsRef}
        id="pillars"
        className="py-20 bg-white relative overflow-hidden"
      >
        {/* Subtle dot pattern background */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(#2d8a8a 1px, transparent 1px)',
            backgroundSize: '24px 24px'
          }}
        />
        {/* Subtle gradient orb behind center */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #2d8a8a 0%, transparent 70%)' }}
        />

        {/* Playbook X element - top left */}
        <svg className="absolute top-20 left-10 w-12 h-12 opacity-[0.04] hidden md:block" viewBox="0 0 40 40">
          <line x1="8" y1="8" x2="32" y2="32" stroke="#1a2332" strokeWidth="3" strokeLinecap="round" />
          <line x1="32" y1="8" x2="8" y2="32" stroke="#1a2332" strokeWidth="3" strokeLinecap="round" />
        </svg>

        {/* Playbook O element - bottom right */}
        <svg className="absolute bottom-32 right-16 w-14 h-14 opacity-[0.03] hidden md:block" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="14" fill="none" stroke="#2d8a8a" strokeWidth="2.5" />
        </svg>

        {/* Playbook play arrow - mid left */}
        <svg className="absolute top-1/2 left-6 w-8 h-8 opacity-[0.04] hidden lg:block" viewBox="0 0 40 40">
          <path d="M10 5 L30 20 L10 35 Z" fill="none" stroke="#2d8a8a" strokeWidth="2" strokeLinejoin="round" />
        </svg>

        <div className={`max-w-6xl mx-auto px-6 relative z-10 transition-all duration-1000 delay-200 ${pillarsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#2d8a8a' }}>Approach</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#1a2332', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Strategy. Systems. Results.</h2>
            <p className="text-lg" style={{ color: '#555' }}>End-to-end transformation, from discovery through delivery.</p>
          </div>

          {/* Process Flow - Timeline */}
          <div className="mb-16">
            {/* Desktop: Horizontal Timeline */}
            <div className="hidden md:block">
              <div className="relative flex items-center justify-between max-w-4xl mx-auto">
                {/* Connecting line */}
                <div
                  className="absolute top-6 left-[8%] right-[8%] h-0.5"
                  style={{ backgroundColor: '#e0eded' }}
                />

                {[
                  { icon: Search, label: 'UNDERSTAND' },
                  { icon: Map, label: 'MAP' },
                  { icon: PenTool, label: 'DESIGN' },
                  { icon: Wrench, label: 'BUILD' },
                  { icon: Rocket, label: 'SHIP' },
                  { icon: RefreshCw, label: 'TRANSFORM' }
                ].map((step, index) => {
                  const IconComponent = step.icon;
                  return (
                    <div key={index} className="group flex flex-col items-center relative z-10">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110 bg-white"
                        style={{ border: '3px solid #2d8a8a' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#2d8a8a';
                          e.currentTarget.querySelector('svg').style.color = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#ffffff';
                          e.currentTarget.querySelector('svg').style.color = '#2d8a8a';
                        }}
                      >
                        <IconComponent className="w-5 h-5 transition-colors duration-300" style={{ color: '#2d8a8a' }} />
                      </div>
                      <span
                        className="text-xs font-semibold tracking-wider transition-colors duration-300"
                        style={{ color: '#1a2332' }}
                      >
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile: Vertical Timeline */}
            <div className="md:hidden">
              <div className="relative pl-20">
                {/* Vertical line */}
                <div
                  className="absolute left-[30px] top-4 bottom-4 w-1 rounded-full"
                  style={{ backgroundColor: '#2d8a8a', opacity: 0.2 }}
                />

                {[
                  { icon: Search, label: 'UNDERSTAND', desc: 'Discovery & Research' },
                  { icon: Map, label: 'MAP', desc: 'Current State Analysis' },
                  { icon: PenTool, label: 'DESIGN', desc: 'Solution Architecture' },
                  { icon: Wrench, label: 'BUILD', desc: 'Development & Config' },
                  { icon: Rocket, label: 'SHIP', desc: 'Deploy & Launch' },
                  { icon: RefreshCw, label: 'TRANSFORM', desc: 'Optimize & Scale' }
                ].map((step, index) => {
                  const IconComponent = step.icon;
                  return (
                    <div key={index} className="flex items-center mb-8 last:mb-0">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center bg-white relative z-10 flex-shrink-0 -ml-20 shadow-lg"
                        style={{ border: '3px solid #2d8a8a' }}
                      >
                        <IconComponent className="w-6 h-6" style={{ color: '#2d8a8a' }} />
                      </div>
                      <div className="ml-5">
                        <span
                          className="text-base font-bold tracking-wide block"
                          style={{ color: '#1a2332' }}
                        >
                          {step.label}
                        </span>
                        <span className="text-sm" style={{ color: '#666' }}>
                          {step.desc}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Strategy */}
            <div className="rounded-xl p-8 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl border-2 border-transparent hover:border-[#2d8a8a]" style={{ backgroundColor: '#f8fafa', border: '1px solid #e0eded' }}>
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
                style={{ backgroundColor: '#2d8a8a' }}
              >
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#1a2332', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Strategy</h3>
              <p className="mb-4 text-sm" style={{ color: '#666' }}>
                Understanding the current state and designing the future
              </p>
              <ul className="space-y-2">
                {strategyItems.map((item, index) => (
                  <li key={index} className="flex items-start text-sm" style={{ color: '#444' }}>
                    <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: '#2d8a8a' }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Systems */}
            <div className="rounded-xl p-8 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl border-2 border-transparent hover:border-[#2d8a8a]" style={{ backgroundColor: '#f8fafa', border: '1px solid #e0eded' }}>
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
                style={{ backgroundColor: '#2d8a8a' }}
              >
                <Cpu className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#1a2332', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Systems</h3>
              <p className="mb-4 text-sm" style={{ color: '#666' }}>
                Building what gets recommended
              </p>
              <ul className="space-y-2">
                {systemsItems.map((item, index) => (
                  <li key={index} className="flex items-start text-sm" style={{ color: '#444' }}>
                    <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: '#2d8a8a' }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Results */}
            <div className="rounded-xl p-8 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl border-2 border-transparent hover:border-[#2d8a8a]" style={{ backgroundColor: '#f8fafa', border: '1px solid #e0eded' }}>
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
                style={{ backgroundColor: '#2d8a8a' }}
              >
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#1a2332', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Results</h3>
              <p className="mb-6 text-sm" style={{ color: '#666' }}>
                Outcomes that matter
              </p>
              <ul className="space-y-2">
                {resultsItems.map((item, index) => (
                  <li key={index} className="flex items-start text-sm" style={{ color: '#444' }}>
                    <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: '#2d8a8a' }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Engagements Section */}
      <Engagements />


      {/* Tools & Technologies */}
      <div
        ref={techRef}
        className="py-16 relative"
        style={{ backgroundColor: '#ffffff' }}
      >
        <div className={`max-w-4xl mx-auto px-6 relative z-10 transition-all duration-1000 delay-200 ${techVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: '#1a2332', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Tools & Technologies</h2>

          <div className="flex flex-wrap justify-center gap-2">
            {techCategories.flatMap(category => category.items).map((item, idx) => (
              <span
                key={idx}
                className="px-3 py-1.5 rounded-full text-sm border transition-all duration-300 cursor-default hover:shadow-md"
                style={{
                  color: '#444',
                  borderColor: '#e0eded',
                  backgroundColor: '#f8fafa'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#2d8a8a';
                  e.target.style.color = '#2d8a8a';
                  e.target.style.backgroundColor = '#e8f4f4';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e0eded';
                  e.target.style.color = '#444';
                  e.target.style.backgroundColor = '#f8fafa';
                }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>


      {/* Credentials Section */}
      <div
        ref={credentialsRef}
        className="py-20 bg-white relative overflow-hidden"
      >
        {/* Playbook elements */}
        <svg className="absolute top-24 left-12 w-10 h-10 opacity-[0.03] hidden md:block" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="13" fill="none" stroke="#2d8a8a" strokeWidth="2" />
        </svg>
        <svg className="absolute bottom-16 right-20 w-12 h-12 opacity-[0.04] hidden md:block" viewBox="0 0 40 40">
          <line x1="6" y1="6" x2="34" y2="34" stroke="#1a2332" strokeWidth="2" strokeLinecap="round" />
          <line x1="34" y1="6" x2="6" y2="34" stroke="#1a2332" strokeWidth="2" strokeLinecap="round" />
        </svg>
        {/* Play route line */}
        <svg className="absolute top-1/2 right-8 w-6 h-20 opacity-[0.03] hidden lg:block" viewBox="0 0 20 60">
          <path d="M10 5 L10 30 L5 40 L10 55" fill="none" stroke="#2d8a8a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>

        <div className={`max-w-4xl mx-auto px-6 relative z-10 transition-all duration-1000 delay-200 ${credentialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#1a2332', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Credentials</h2>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Certifications */}
            <div className="md:col-span-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide mb-4 flex items-center" style={{ color: '#666' }}>
                <Award className="w-4 h-4 mr-2" style={{ color: '#2d8a8a' }} />
                Certifications
              </h3>
              <div className="flex flex-wrap gap-2">
                {certifications.map((cert, index) => (
                  <span
                    key={index}
                    className="bg-white px-3 py-2 rounded-lg text-sm border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
                    style={{ color: '#333' }}
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>

            {/* Education */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide mb-4 flex items-center" style={{ color: '#666' }}>
                <GraduationCap className="w-4 h-4 mr-2" style={{ color: '#2d8a8a' }} />
                Education
              </h3>
              <p className="font-medium" style={{ color: '#1a2332' }}>DePaul University</p>
              <p className="text-sm" style={{ color: '#666' }}>BSB in Business Management</p>
              <p className="text-sm" style={{ color: '#666' }}>Concentration in Entrepreneurship</p>
            </div>
          </div>

          {/* Current Role */}
          <div className="text-center pt-8 border-t border-gray-100">
            <h3 className="text-sm font-semibold uppercase tracking-wide mb-2 flex items-center justify-center" style={{ color: '#666' }}>
              <Briefcase className="w-4 h-4 mr-2" style={{ color: '#2d8a8a' }} />
              Current Role
            </h3>
            <p className="font-medium" style={{ color: '#1a2332' }}>Senior Consultant at Slalom | AI Navigator</p>
          </div>
        </div>
      </div>


      {/* Contact Section */}
      <div
        ref={contactRef}
        id="contact"
        className="py-20 relative overflow-hidden"
        style={{ backgroundColor: '#1a2332' }}
      >
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(#2d8a8a 1px, transparent 1px), linear-gradient(90deg, #2d8a8a 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />
        {/* Decorative floating elements */}
        <div
          className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-15"
          style={{ backgroundColor: '#2d8a8a', transform: 'translate(30%, -30%)', filter: 'blur(60px)', animation: 'float 10s ease-in-out infinite' }}
        />
        <div
          className="absolute bottom-0 left-0 w-56 h-56 rounded-full opacity-10"
          style={{ backgroundColor: '#2d8a8a', transform: 'translate(-30%, 30%)', filter: 'blur(50px)', animation: 'float 8s ease-in-out infinite', animationDelay: '-4s' }}
        />
        <div
          className="absolute top-1/2 left-1/4 w-32 h-32 rounded-full opacity-5 hidden md:block"
          style={{ backgroundColor: '#4a9e9e', filter: 'blur(30px)', animation: 'float 6s ease-in-out infinite', animationDelay: '-2s' }}
        />

        <div className={`max-w-3xl mx-auto px-6 text-center relative z-10 transition-all duration-1000 delay-200 ${contactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Connect With Me</h2>
          <p className="text-xl mb-10" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Open to conversations about technology transformation, AI strategy, and implementation projects.
          </p>

          <div className="flex flex-wrap justify-center gap-8 mb-10">
            <a
              href="https://linkedin.com/in/BorisBlinderman"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center transition-all duration-300 hover:scale-105"
              style={{ color: 'rgba(255,255,255,0.7)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#2d8a8a'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'linkedin_click', {
                    location: 'contact_section'
                  });
                }
              }}
            >
              <Linkedin className="w-5 h-5 mr-2" />
              linkedin.com/in/BorisBlinderman
            </a>
            <a
              href={'mailto:' + ['BorisBlinderman', 'gmail.com'].join('@')}
              className="flex items-center transition-all duration-300 hover:scale-105"
              style={{ color: 'rgba(255,255,255,0.7)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#2d8a8a'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'email_click', {
                    location: 'contact_section'
                  });
                }
              }}
            >
              <Mail className="w-5 h-5 mr-2" />
              {['BorisBlinderman', 'gmail.com'].join('@')}
            </a>
            <span className="flex items-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <MapPin className="w-5 h-5 mr-2" />
              Orange County, CA
            </span>
          </div>

          <button
            onClick={() => {
              setShowScheduleModal(true);
              if (window.gtag) {
                window.gtag('event', 'schedule_meeting_click', {
                  location: 'contact_section'
                });
              }
            }}
            className="inline-flex items-center px-8 py-4 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            style={{ backgroundColor: '#2d8a8a', color: 'white' }}
          >
            <Calendar className="mr-2 h-5 w-5" />
            Schedule a Meeting
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-10 px-6" style={{ backgroundColor: '#0f1419' }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <span className="text-xl font-semibold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Boris Blinderman</span>
              <p className="mt-1" style={{ color: '#666' }}>From Roadmap to Production</p>
            </div>
            <div className="flex space-x-6">
              <a
                href="https://linkedin.com/in/BorisBlinderman"
                className="transition-colors duration-300"
                style={{ color: '#666' }}
                onMouseEnter={(e) => e.target.style.color = '#2d8a8a'}
                onMouseLeave={(e) => e.target.style.color = '#666'}
              >
                LinkedIn
              </a>
              <a
                href={'mailto:' + ['BorisBlinderman', 'gmail.com'].join('@')}
                className="transition-colors duration-300"
                style={{ color: '#666' }}
                onMouseEnter={(e) => e.target.style.color = '#2d8a8a'}
                onMouseLeave={(e) => e.target.style.color = '#666'}
              >
                Email
              </a>
              <button
                onClick={() => setShowResumeModal(true)}
                className="transition-colors duration-300"
                style={{ color: '#666' }}
                onMouseEnter={(e) => e.target.style.color = '#2d8a8a'}
                onMouseLeave={(e) => e.target.style.color = '#666'}
              >
                Resume
              </button>
            </div>
          </div>
          {/* Outside of Work */}
          <div className="border-t mt-6 pt-6 text-center" style={{ borderColor: '#1a2332' }}>
            <p className="text-sm" style={{ color: '#888' }}>
              Chicago-raised, California transplant since 2015. CrossFit, pickleball, volleyball, tennis, and weekend drives between snowy mountains and Laguna Beach.
            </p>
          </div>
          <div className="border-t mt-6 pt-6 text-center text-sm" style={{ borderColor: '#1a2332', color: '#444' }}>
            © {new Date().getFullYear()} Boris Blinderman
          </div>
        </div>
      </footer>

      {/* Go to Top Button */}
      <button
        onClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          if (window.gtag) {
            window.gtag('event', 'scroll_to_top_click');
          }
        }}
        className={`fixed bottom-6 right-6 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl z-50 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        style={{
          background: 'linear-gradient(135deg, #2d8a8a, #1a2332)',
          color: 'white'
        }}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-5 h-5" />
      </button>

      {/* Resume Modal */}
      {showResumeModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setShowResumeModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          {/* Modal Content - Responsive */}
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-[95vw] md:h-[95vh] max-h-[95vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-200" style={{ backgroundColor: '#f8fafa' }}>
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5" style={{ color: '#2d8a8a' }} />
                <span className="font-semibold text-sm md:text-base" style={{ color: '#1a2332' }}>Resume</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Share Button - Icon only on mobile */}
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: 'Boris Blinderman - Resume',
                        text: 'Check out Boris Blinderman\'s resume',
                        url: window.location.origin + '/Boris_Blinderman_Resume.pdf'
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.origin + '/Boris_Blinderman_Resume.pdf');
                      alert('Resume link copied to clipboard!');
                    }
                    if (window.gtag) {
                      window.gtag('event', 'resume_share', {
                        location: 'resume_modal'
                      });
                    }
                  }}
                  className="flex items-center gap-2 px-2 md:px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:shadow-md"
                  style={{ backgroundColor: '#e8f4f4', color: '#2d8a8a' }}
                >
                  <Share2 className="w-4 h-4" />
                  <span className="hidden md:inline">Share</span>
                </button>
                {/* Download Button - Icon only on mobile */}
                <a
                  href="/Boris_Blinderman_Resume.pdf"
                  download="Boris_Blinderman_Resume.pdf"
                  className="flex items-center gap-2 px-2 md:px-3 py-2 rounded-lg text-sm font-medium text-white transition-all duration-300 hover:shadow-md"
                  style={{ backgroundColor: '#2d8a8a' }}
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'resume_download', {
                        location: 'resume_modal_header'
                      });
                    }
                  }}
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden md:inline">Download</span>
                </a>
                {/* Close Button */}
                <button
                  onClick={() => setShowResumeModal(false)}
                  className="p-2 rounded-lg transition-colors duration-300 hover:bg-gray-100"
                  style={{ color: '#666' }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Mobile View - Action buttons instead of PDF */}
            <div className="md:hidden flex-1 flex flex-col items-center justify-center p-8 bg-gray-50">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#e8f4f4' }}>
                <FileText className="w-10 h-10" style={{ color: '#2d8a8a' }} />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#1a2332' }}>Boris Blinderman</h3>
              <p className="text-sm mb-8 text-center" style={{ color: '#666' }}>AI Strategy Consultant | Senior Consultant at Slalom</p>

              <div className="flex flex-col gap-3 w-full max-w-xs">
                <a
                  href="/Boris_Blinderman_Resume.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-white font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{ backgroundColor: '#2d8a8a' }}
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'resume_view', {
                        location: 'resume_modal_mobile'
                      });
                    }
                  }}
                >
                  <FileText className="w-5 h-5" />
                  View Resume
                </a>
                <a
                  href="/Boris_Blinderman_Resume.pdf"
                  download="Boris_Blinderman_Resume.pdf"
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-medium transition-all duration-300 border-2"
                  style={{ borderColor: '#2d8a8a', color: '#2d8a8a' }}
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'resume_download', {
                        location: 'resume_modal_mobile'
                      });
                    }
                  }}
                >
                  <Download className="w-5 h-5" />
                  Download PDF
                </a>
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: 'Boris Blinderman - Resume',
                        text: 'Check out Boris Blinderman\'s resume',
                        url: window.location.origin + '/Boris_Blinderman_Resume.pdf'
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.origin + '/Boris_Blinderman_Resume.pdf');
                      alert('Resume link copied to clipboard!');
                    }
                  }}
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-medium transition-all duration-300 border-2"
                  style={{ borderColor: '#1a2332', color: '#1a2332' }}
                >
                  <Share2 className="w-5 h-5" />
                  Share Resume
                </button>
              </div>
            </div>

            {/* Desktop View - PDF Viewer */}
            <div className="hidden md:block flex-1 overflow-hidden bg-gray-100">
              <iframe
                src="/Boris_Blinderman_Resume.pdf#toolbar=0&navpanes=0&scrollbar=0"
                title="Resume"
                className="w-full h-full"
                style={{ border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setShowScheduleModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          {/* Modal Content - Full Screen */}
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-[95vw] h-[90vh] max-w-4xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ backgroundColor: '#f8fafa' }}>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5" style={{ color: '#2d8a8a' }} />
                <span className="font-semibold" style={{ color: '#1a2332' }}>Schedule a Meeting</span>
              </div>
              <button
                onClick={() => setShowScheduleModal(false)}
                className="p-2 rounded-lg transition-colors duration-300 hover:bg-gray-100"
                style={{ color: '#666' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Zcal Embed */}
            <div className="flex-1 overflow-hidden">
              <iframe
                src="https://zcal.co/emb/borisblinderman?embed=1&embedType=iframe"
                title="Schedule a Meeting"
                className="w-full h-full"
                style={{ border: 'none', minHeight: '544px' }}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default PortfolioHome;
